import { Edit3, Eye, MoreVertical, Pin, PinOff, Reply, Trash2 } from 'lucide-react'
import * as React from 'react'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import { usePrefersHover } from '@/hooks/use-prefers-hover'

import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

import {
  getMessageActionButtonsClassName,
  shouldIgnoreMessageActivation,
} from '@/utils/chat-message-action-visibility'
import { hasAttachmentWithCaption, isAttachmentMessageType } from '@/utils/chat-message-content'
import {
  getMessageSeriesBubbleClassName,
  getMessageSeriesBubblePaddingClassName,
  MessageSeriesPosition,
} from '@/utils/chat-message-series'
import { MessageViewer } from '@/utils/chat-message-viewers'
import { formatTimestampDivider } from '@/utils/chat-timestamp'

import { ChatMessageFileContentItem, ChatMessageImageContentItem, ChatMessageVideoContentItem, FileMessage } from './chat-messages'
import { MessageActionButtonsContainer } from './message-action-buttons-container'
import { MessageAttachmentTextSplit } from './message-attachment-text-split'
import { MessageReactionIconsRow } from './message-reaction-container'
import MessageReactions from './message-reactions'
import { LatestMessageViewerAvatars, MessageViewersHover } from './message-viewers'
import { ReplyMessageBubble } from './replied-message-preview'

import { Message, MessageEmojiData, MessageReactionType, MessageStatus } from '@/types/chat.type'
import { UserProfile } from '@/types/user.type'

// Component to render emoji content with proper sizing
const EmojiContentRenderer: React.FC<{
  content: string
  isEmojiOnly: boolean
  isOwnMessage: boolean
  onCopy: (e: React.ClipboardEvent) => void
}> = ({ content, isOwnMessage, onCopy }) => {
  const contentRef = React.useRef<HTMLDivElement>(null)

  // Check if content truly has only icons/emojis (no regular text)
  const hasOnlyIcons = React.useMemo(() => {
    // Remove all img tags to check remaining content
    const contentWithoutImages = content.replace(/<img[^>]*>/gi, '')
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = contentWithoutImages
    const textWithoutImages = tempDiv.textContent || tempDiv.innerText || ''

    // Check if there are any img tags (icons/emojis)
    const hasImages = /<img[^>]*>/gi.test(content)

    // If there are images and no text, or only emoji Unicode characters
    if (hasImages && textWithoutImages.trim().length === 0) {
      return true
    }

    // Check if remaining text is only Unicode emojis
    if (textWithoutImages.trim().length > 0) {
      // Unicode emoji regex pattern (covers most emoji ranges)
      // Using a pattern that matches emoji characters without requiring the 'u' flag
      // eslint-disable-next-line no-misleading-character-class
      const emojiRegex =
        /(?:\uD83C[\uDF00-\uDFFF]|\uD83D[\uDC00-\uDE4F]|\uD83D[\uDE80-\uDEFF]|[\u2600-\u27BF]|\uD83E[\uDD00-\uDDFF])/g
      const textWithoutEmojis = textWithoutImages.trim().replace(emojiRegex, '').trim()
      return textWithoutEmojis.length === 0
    }

    return false
  }, [content])

  React.useEffect(() => {
    if (!contentRef.current) return

    const mentionLinks = contentRef.current.querySelectorAll('.mention')
    mentionLinks.forEach(link => {
      const mentionElement = link as HTMLElement
      mentionElement.style.setProperty('color', isOwnMessage ? '#ffffff' : '#1d4ed8', 'important')
      mentionElement.style.setProperty('font-weight', '700', 'important')
    })

    const images = contentRef.current.querySelectorAll('img')
    images.forEach(img => {
      // Only apply size constraints when icons are alone (no characters)
      if (hasOnlyIcons) {
        // Override inline styles to make emojis bigger
        img.style.width = '120px'
        img.style.height = '120px'
        img.style.minWidth = '120px'
        img.style.minHeight = '120px'
        img.style.maxWidth = '120px'
        img.style.maxHeight = '120px'

        // For larger screens
        if (window.innerWidth >= 640) {
          img.style.width = '150px'
          img.style.height = '150px'
          img.style.minWidth = '150px'
          img.style.minHeight = '150px'
          img.style.maxWidth = '150px'
          img.style.maxHeight = '150px'
        }
      } else {
        // Remove size constraints when icons go along with characters
        img.style.width = '24px'
        img.style.height = '24px'
        img.style.minWidth = '24px'
        img.style.minHeight = '24px'
      }
    })

    // Make Unicode emoji characters bigger only if there are only icons
    if (hasOnlyIcons) {
      const walker = document.createTreeWalker(contentRef.current, NodeFilter.SHOW_TEXT, null)

      let node
      while ((node = walker.nextNode())) {
        if (node.textContent && node.textContent.trim().length > 0) {
          // Check if text contains emoji characters
          // Using a pattern that matches emoji characters without requiring the 'u' flag
          // eslint-disable-next-line no-misleading-character-class
          const emojiRegex =
            /(?:\uD83C[\uDF00-\uDFFF]|\uD83D[\uDC00-\uDE4F]|\uD83D[\uDE80-\uDEFF]|[\u2600-\u27BF]|\uD83E[\uDD00-\uDDFF])/g
          if (emojiRegex.test(node.textContent)) {
            const parent = node.parentElement
            if (parent && parent.tagName !== 'IMG') {
              parent.style.fontSize = '60px'
              if (window.innerWidth >= 640) {
                parent.style.fontSize = '60px'
              }
            }
          }
        }
      }
    }
  }, [content, hasOnlyIcons, isOwnMessage])

  return (
    <div
      ref={contentRef}
      className={`w-full whitespace-pre-wrap break-words [overflow-wrap:anywhere] [word-break:break-word] hyphens-auto [&_img]:select-none [&_img]:pointer-events-none [&_.mention]:font-bold [&_.mention]:no-underline [&_.mention:hover]:underline ${
        isOwnMessage
          ? '[&_.mention]:text-white [&_.mention:hover]:text-white'
          : '[&_.mention]:text-blue-700 [&_.mention:hover]:text-blue-700'
      }`}
      style={hasOnlyIcons ? { fontSize: '60px', lineHeight: '1.2' } : undefined}
      dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(content) }}
      onCopy={onCopy}
    />
  )
}

interface MessageBoxWithActionsProps {
  message: Message
  isOwnMessage: boolean
  isSystemMessage: boolean
  editingMessageId: number | null
  editMessageContent: string
  onStartEditMessage: (messageId: number, currentContent: string) => void
  onEditMessage: () => void
  onCancelEditMessage: () => void
  onEditMessageContentChange: (content: string) => void
  onShowDeleteConfirm: (messageId: number) => void
  onStartReply: (message: Message) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderFileMessage: (message: any) => FileMessage | null
  formatTimestamp: (timestamp: string) => string
  getMessageViewers: (message: Message) => MessageViewer[]
  setViewingMessage: (viewers: { viewers: MessageViewer[] }) => void
  isLatestMessage?: boolean
  seriesPosition?: MessageSeriesPosition
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  scrollManager?: any
  onScrollToMessage?: (messageId: number) => void
  onMessageReact?: (messageId: number, reactionType: MessageReactionType, emojiData?: MessageEmojiData) => void
  currentUser?: UserProfile
  onPinMessage?: (messageId: number) => void
  onUnpinMessage?: (messageId: number) => void
  conversationId?: number
}

export const MessageBoxWithActions: React.FC<MessageBoxWithActionsProps> = ({
  message,
  isOwnMessage,
  isSystemMessage,
  editingMessageId,
  editMessageContent,
  onStartEditMessage,
  onEditMessage,
  onCancelEditMessage,
  onEditMessageContentChange,
  onShowDeleteConfirm,
  onStartReply,
  renderFileMessage,
  formatTimestamp: _formatTimestamp,
  getMessageViewers,
  setViewingMessage,
  isLatestMessage = false,
  seriesPosition = 'single',
  onScrollToMessage,
  onMessageReact,
  currentUser,
  onPinMessage,
  onUnpinMessage,
  conversationId,
  scrollManager,
}) => {
  const prefersHover = usePrefersHover()
  const messageActionsRef = React.useRef<HTMLDivElement>(null)
  const [isActionsActive, setIsActionsActive] = React.useState(false)
  const [isHoveringActions, setIsHoveringActions] = React.useState(false)

  const isActionButtonsVisible = isActionsActive || (prefersHover && isHoveringActions)
  const messagesContainerRef = scrollManager?.messagesContainerRef as React.RefObject<HTMLElement | null> | undefined

  const actionButtonsClassName = React.useMemo(
    () => getMessageActionButtonsClassName({ prefersHover, isActive: isActionsActive }),
    [prefersHover, isActionsActive]
  )

  React.useEffect(() => {
    if (prefersHover || !isActionsActive) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!messageActionsRef.current?.contains(event.target as Node)) {
        setIsActionsActive(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [prefersHover, isActionsActive])

  const handleMessageActivate = React.useCallback(
    (event: React.MouseEvent) => {
      if (prefersHover || shouldIgnoreMessageActivation(event.target)) {
        return
      }
      setIsActionsActive(previous => !previous)
    },
    [prefersHover]
  )

  const sanitizedMessageContent = React.useMemo(() => sanitizeHtmlValue(message.content || ''), [message.content])
  const viewers = React.useMemo(() => getMessageViewers(message), [getMessageViewers, message])
  const hasViewers = viewers.length > 0
  const seriesBubbleClassName = getMessageSeriesBubbleClassName(seriesPosition, isOwnMessage)
  const seriesBubblePaddingClassName = getMessageSeriesBubblePaddingClassName(seriesPosition)

  const bubbleBaseClassName =
    'relative px-3 py-2 sm:px-4 sm:py-2 text-xs sm:text-sm transition-all duration-300 hover:shadow-sm min-w-0'
  const getOwnMessageStatusClassName = () => {
    if (editingMessageId === message.id) {
      return 'bg-blue-50 border-2 border-blue-200 text-gray-900'
    }
    if (message.status === MessageStatus.ERROR) {
      return 'bg-red-100 border border-red-300 text-red-800'
    }
    if (message.status === MessageStatus.PENDING) {
      return 'bg-blue-600 text-white opacity-70'
    }
    return 'bg-blue-600 text-white group-hover:bg-blue-700'
  }

  const getOwnMessageBubbleClassName = () =>
    `${bubbleBaseClassName} ${seriesBubbleClassName} ${seriesBubblePaddingClassName} ${getOwnMessageStatusClassName()}`

  const getOwnMessageReplyBubbleClassName = () => `${getOwnMessageStatusClassName()} ${seriesBubblePaddingClassName}`

  const getOtherMessageStatusClassName = () => {
    if (isSystemMessage) {
      return 'bg-gray-100 text-gray-600 text-center mx-auto text-xs sm:text-sm'
    }
    return 'bg-gray-100 text-gray-900 group-hover:bg-gray-200'
  }

  const getOtherMessageReplyBubbleClassName = () => {
    if (isSystemMessage) {
      return `bg-gray-100 text-gray-600 ${seriesBubblePaddingClassName}`
    }
    return `border border-gray-200 bg-white text-gray-900 group-hover:bg-gray-50 ${seriesBubblePaddingClassName}`
  }

  const getOtherMessageBubbleClassName = () =>
    `${bubbleBaseClassName} ${seriesBubbleClassName} ${seriesBubblePaddingClassName} ${getOtherMessageStatusClassName()}`

  const isReplyLayout = Boolean(message.repliedMessage && editingMessageId !== message.id)

  const renderMessageStatusExtras = (): React.ReactNode => (
    <>
      {message.status === MessageStatus.ERROR && message.errorMessage && (
        <div className="mt-2 block w-full max-w-full text-xs text-red-600 font-medium whitespace-pre-wrap break-words [overflow-wrap:anywhere] [word-break:break-word]">
          {message.errorMessage}
        </div>
      )}
      {message.status === MessageStatus.PENDING && (
        <div className="mt-2 text-xs text-blue-300 font-medium flex items-center gap-1">
          <div className="w-2 h-2 bg-blue-300 rounded-full animate-pulse"></div>
          Sending...
        </div>
      )}
    </>
  )

  const renderPinnedBadge = () => {
    if (!message.pinnedMessage) return null

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-sm border border-gray-200">
              <Pin className="w-3 h-3 text-blue-600" />
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="text-xs">
              <div className="font-semibold">Pinned message</div>
              {message.pinnedMessage.pinnedByUser && (
                <div className="text-gray-600 mt-1">
                  By {message.pinnedMessage.pinnedByUser.firstName} {message.pinnedMessage.pinnedByUser.lastName}
                </div>
              )}
              <div className="text-gray-500 mt-0.5">{formatTimestampDivider(message.pinnedMessage.pinnedAt)}</div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  const renderBubbleExtras = (): React.ReactNode => (
    <>
      {renderMessageStatusExtras()}
      {renderPinnedBadge()}
    </>
  )

  const renderReactionIcons = () => (
    <MessageReactionIconsRow
      messageId={message.id}
      reactions={message.reactions || []}
      onReact={onMessageReact}
      currentUser={currentUser}
      isOwnMessage={isOwnMessage}
      isSystemMessage={isSystemMessage}
    />
  )

  const wrapWithViewersHover = (content: React.ReactNode) => {
    if (!hasViewers) return content
    return <MessageViewersHover viewers={viewers}>{content as React.ReactElement}</MessageViewersHover>
  }

  const renderLatestViewerAvatars = () => {
    if (!isOwnMessage || !isLatestMessage || !hasViewers) return null
    return (
      <LatestMessageViewerAvatars
        viewers={viewers}
        align="end"
        onClick={() => setViewingMessage({ viewers })}
      />
    )
  }

  const handleCopy = React.useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault()

      const stripHtmlAndImages = (html: string) => {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = html

        const images = tempDiv.querySelectorAll('img')
        images.forEach(img => img.remove())

        const imageElements = tempDiv.querySelectorAll('[class*="image"], [class*="img"], [style*="background-image"]')
        imageElements.forEach(el => el.remove())

        return tempDiv.textContent || tempDiv.innerText || ''
      }

      const plainText = stripHtmlAndImages(sanitizedMessageContent)

      e.clipboardData.setData('text/plain', plainText)
    },
    [sanitizedMessageContent]
  )

  const hasAttachmentAndCaption = React.useMemo(
    () => hasAttachmentWithCaption(message.type, sanitizedMessageContent, message.fileName),
    [message.type, message.fileName, sanitizedMessageContent]
  )

  // Check if message is file/image-only (no text content)
  const isFileImageOnlyMessage = React.useMemo(() => {
    if (!isAttachmentMessageType(message.type)) {
      return false
    }

    return !hasAttachmentAndCaption
  }, [hasAttachmentAndCaption, message.type])

  const isEmojiOnlyMessage = React.useMemo(() => {
    if (isAttachmentMessageType(message.type)) {
      return false
    }

    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = sanitizedMessageContent
    const plainText = tempDiv.textContent || tempDiv.innerText || ''

    const contentWithoutImages = sanitizedMessageContent.replace(/<img[^>]*>/gi, '')
    const tempDiv2 = document.createElement('div')
    tempDiv2.innerHTML = contentWithoutImages
    const textWithoutImages = tempDiv2.textContent || tempDiv2.innerText || ''

    const hasEmojiImages =
      /<img[^>]*class="emoji"[^>]*>/gi.test(sanitizedMessageContent) || /<img[^>]*>/gi.test(sanitizedMessageContent)
    const emojiRegex =
      /(?:\uD83C[\uDF00-\uDFFF]|\uD83D[\uDC00-\uDE4F]|\uD83D[\uDE80-\uDEFF]|[\u2600-\u27BF]|\uD83E[\uDD00-\uDDFF])/g

    const textOnlyEmojis = plainText.trim().replace(emojiRegex, '').trim().length === 0 && plainText.trim().length > 0

    return (hasEmojiImages && textWithoutImages.trim().length === 0) || (textOnlyEmojis && plainText.trim().length > 0)
  }, [sanitizedMessageContent, message.type])

  const renderMessageContent = () => {
    if (editingMessageId === message.id) {
      return (
        <div className="space-y-2">
          <div className="text-xs text-blue-600 font-medium mb-1">✏️ Editing message...</div>
          <div className="relative">
            <textarea
              value={editMessageContent}
              onChange={e => onEditMessageContentChange(e.target.value)}
              className="text-xs sm:text-sm border-blue-300 focus:border-blue-500 focus:ring-blue-500 bg-white pr-16 w-full"
              autoFocus
              placeholder="Edit your message..."
              maxLength={500}
            />
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">
              {editMessageContent.length}/{500}
            </div>
          </div>
          <div className="flex gap-1">
            <Button
              size="sm"
              onClick={onEditMessage}
              className="text-xs bg-white hover:bg-red-50 text-gray-900 border border-gray-300 hover:border-red-300 hover:text-red-700"
              disabled={
                !editMessageContent.trim() || editMessageContent === message.content || editMessageContent.length > 500
              }
            >
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onCancelEditMessage}
              className="text-xs border-gray-300 text-gray-700 hover:bg-red-50 hover:border-red-300 hover:text-red-700"
            >
              Cancel
            </Button>
          </div>
        </div>
      )
    }

    const renderFileAttachment = () => {
      const fileData = renderFileMessage(message)
      if (!fileData) return null

      if (fileData.isImage) {
        return <ChatMessageImageContentItem fileData={fileData} />
      }
      if (fileData.isVideo) {
        return <ChatMessageVideoContentItem fileData={fileData} />
      }
      return <ChatMessageFileContentItem fileData={fileData} />
    }

    const renderTextBody = () => (
      <EmojiContentRenderer
        content={sanitizedMessageContent}
        isEmojiOnly={isEmojiOnlyMessage}
        isOwnMessage={isOwnMessage}
        onCopy={handleCopy}
      />
    )

    if (hasAttachmentAndCaption) {
      return (
        <MessageAttachmentTextSplit
          isOwnMessage={isOwnMessage}
          seriesPosition={seriesPosition}
          attachment={renderFileAttachment()}
          text={renderTextBody()}
          textBubbleClassName={isOwnMessage ? getOwnMessageStatusClassName() : getOtherMessageStatusClassName()}
          extras={renderBubbleExtras()}
        />
      )
    }

    return (
      <div>
        {isAttachmentMessageType(message.type) ? renderFileAttachment() : renderTextBody()}
      </div>
    )
  }

  const renderActionButtons = () => {
    if (isOwnMessage) {
      return (
        <MessageActionButtonsContainer
          className={actionButtonsClassName}
          isVisible={isActionButtonsVisible}
          boundaryRef={messagesContainerRef}
        >
          {/* Reply button */}
          {!isSystemMessage && (
            <button
              onClick={() => onStartReply(message)}
              className="p-1.5 hover:bg-gray-200 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Reply to message"
              disabled={editingMessageId === message.id || message.isSystem}
            >
              <Reply className="w-3 h-3 text-gray-500 hover:text-blue-600 transition-colors duration-200" />
            </button>
          )}

          {/* Reaction button */}
          {!isSystemMessage && onMessageReact && (
            <div className="inline-flex">
              <MessageReactions
                messageId={message.id}
                reactions={message.reactions || []}
                onReact={onMessageReact}
                currentUser={currentUser}
                isOwnMessage={isOwnMessage}
                buttonOnly={true}
              />
            </div>
          )}

          {/* Action menu */}
          {!isSystemMessage && (
            <div className="flex items-center">
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-1.5 hover:bg-gray-200 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed relative z-10"
                    disabled={editingMessageId === message.id || message.isSystem}
                  >
                    <MoreVertical className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 z-[9999] relative">
                  {/* Pin/Unpin option */}
                  {conversationId && (onPinMessage || onUnpinMessage) && (
                    <DropdownMenuItem
                      onClick={() => {
                        if (message.pinnedMessage && onUnpinMessage) {
                          onUnpinMessage(message.id)
                        } else if (!message.pinnedMessage && onPinMessage) {
                          onPinMessage(message.id)
                        }
                      }}
                      disabled={editingMessageId === message.id || message.isSystem}
                      className="cursor-pointer"
                    >
                      {message.pinnedMessage ? (
                        <>
                          <PinOff className="w-4 h-4 mr-2 text-gray-500" />
                          Unpin
                        </>
                      ) : (
                        <>
                          <Pin className="w-4 h-4 mr-2 text-blue-500" />
                          Pin
                        </>
                      )}
                    </DropdownMenuItem>
                  )}

                  {hasViewers && (
                    <DropdownMenuItem
                      onClick={() => setViewingMessage({ viewers })}
                      className="cursor-pointer"
                    >
                      <Eye className="w-4 h-4 mr-2 text-blue-500" />
                      Người đã xem
                    </DropdownMenuItem>
                  )}

                  {/* Edit option */}
                  <DropdownMenuItem
                    onClick={() => onStartEditMessage(message.id, message.content)}
                    disabled={editingMessageId === message.id || message.isSystem}
                    className="cursor-pointer"
                  >
                    <Edit3 className="w-4 h-4 mr-2 text-blue-500" />
                    Edit
                  </DropdownMenuItem>

                  {/* Delete option */}
                  <DropdownMenuItem
                    onClick={() => onShowDeleteConfirm(message.id)}
                    disabled={editingMessageId === message.id || message.isSystem}
                    className="cursor-pointer text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </MessageActionButtonsContainer>
      )
    }

    // Action buttons for other users' messages
    return (
      <MessageActionButtonsContainer
        className={actionButtonsClassName}
        isVisible={isActionButtonsVisible}
        boundaryRef={messagesContainerRef}
      >
        {/* Reply button */}
        {!isSystemMessage && (
          <button
            onClick={() => onStartReply(message)}
            className="p-1.5 hover:bg-gray-200 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Reply to message"
            disabled={editingMessageId === message.id || message.isSystem}
          >
            <Reply className="w-3 h-3 text-gray-500 hover:text-blue-600 transition-colors duration-200" />
          </button>
        )}

        {/* Reaction button */}
        {!isSystemMessage && onMessageReact && (
          <div className="inline-flex">
            <MessageReactions
              messageId={message.id}
              reactions={message.reactions || []}
              onReact={onMessageReact}
              currentUser={currentUser}
              isOwnMessage={isOwnMessage}
              buttonOnly={true}
            />
          </div>
        )}

        {/* Action menu for other users' messages */}
        {!isSystemMessage && conversationId && (onPinMessage || onUnpinMessage || hasViewers) && (
          <div className="flex items-center">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button
                  className="p-1.5 hover:bg-gray-200 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed relative z-10"
                  disabled={editingMessageId === message.id || message.isSystem}
                >
                  <MoreVertical className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 z-[9999] relative">
                {(onPinMessage || onUnpinMessage) && (
                  <DropdownMenuItem
                    onClick={() => {
                      if (message.pinnedMessage && onUnpinMessage) {
                        onUnpinMessage(message.id)
                      } else if (!message.pinnedMessage && onPinMessage) {
                        onPinMessage(message.id)
                      }
                    }}
                    disabled={editingMessageId === message.id || message.isSystem}
                    className="cursor-pointer"
                  >
                    {message.pinnedMessage ? (
                      <>
                        <PinOff className="w-4 h-4 mr-2 text-gray-500" />
                        Unpin
                      </>
                    ) : (
                      <>
                        <Pin className="w-4 h-4 mr-2 text-blue-500" />
                        Pin
                      </>
                    )}
                  </DropdownMenuItem>
                )}
                {hasViewers && (
                  <DropdownMenuItem
                    onClick={() => setViewingMessage({ viewers })}
                    className="cursor-pointer"
                  >
                    <Eye className="w-4 h-4 mr-2 text-blue-500" />
                    Người đã xem
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </MessageActionButtonsContainer>
    )
  }

  const renderMessageBubble = () => {
    if (isOwnMessage) {
      if (
        (isEmojiOnlyMessage || isFileImageOnlyMessage) &&
        !editingMessageId &&
        message.status !== MessageStatus.ERROR &&
        !message.repliedMessage &&
        !message.pinnedMessage
      ) {
        return wrapWithViewersHover(<div className="relative">{renderMessageContent()}</div>)
      }

      if (message.repliedMessage) {
        return wrapWithViewersHover(
          <ReplyMessageBubble
            isOwnMessage={isOwnMessage}
            seriesBubbleClassName={seriesBubbleClassName}
            repliedMessage={message.repliedMessage}
            onScrollToMessage={onScrollToMessage}
            bubbleClassName={getOwnMessageReplyBubbleClassName()}
            splitBubbles={hasAttachmentAndCaption}
            actionButtons={renderActionButtons()}
          >
            {renderMessageContent()}
            {!hasAttachmentAndCaption && renderBubbleExtras()}
          </ReplyMessageBubble>
        )
      }

      if (hasAttachmentAndCaption) {
        return wrapWithViewersHover(<div className="relative">{renderMessageContent()}</div>)
      }

      return wrapWithViewersHover(
        <div className={getOwnMessageBubbleClassName()}>
          {renderMessageContent()}
          {renderBubbleExtras()}
        </div>
      )
    }

    if (
      (isEmojiOnlyMessage || isFileImageOnlyMessage) &&
      !isSystemMessage &&
      !message.repliedMessage &&
      !message.pinnedMessage
    ) {
      return <div className="relative">{renderMessageContent()}</div>
    }

    if (message.repliedMessage) {
      return (
        <ReplyMessageBubble
          isOwnMessage={isOwnMessage}
          seriesBubbleClassName={seriesBubbleClassName}
          repliedMessage={message.repliedMessage}
          onScrollToMessage={onScrollToMessage}
          bubbleClassName={getOtherMessageReplyBubbleClassName()}
          splitBubbles={hasAttachmentAndCaption}
          actionButtons={renderActionButtons()}
        >
          {renderMessageContent()}
          {!hasAttachmentAndCaption && renderBubbleExtras()}
        </ReplyMessageBubble>
      )
    }

    if (hasAttachmentAndCaption) {
      return <div className="relative">{renderMessageContent()}</div>
    }

    return (
      <div className={getOtherMessageBubbleClassName()}>
        {renderMessageContent()}
        {renderBubbleExtras()}
      </div>
    )
  }

  if (isOwnMessage) {
    return (
      <div className="flex max-w-[280px] flex-col items-end sm:max-w-xs lg:max-w-md">
        <div
          ref={messageActionsRef}
          className={isReplyLayout ? 'w-full' : 'flex w-fit max-w-full items-center gap-2'}
          onMouseEnter={() => setIsHoveringActions(true)}
          onMouseLeave={() => setIsHoveringActions(false)}
        >
          {!isReplyLayout && renderActionButtons()}
          <div className="relative min-w-0 w-fit max-w-full" onClick={handleMessageActivate}>
            {renderMessageBubble()}
            {renderLatestViewerAvatars()}
          </div>
        </div>
        {renderReactionIcons()}
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col items-start">
      <div
        ref={messageActionsRef}
        className={isReplyLayout ? 'w-full' : 'flex w-fit max-w-full items-center gap-2'}
        onMouseEnter={() => setIsHoveringActions(true)}
        onMouseLeave={() => setIsHoveringActions(false)}
      >
        <div className="relative min-w-0 w-fit max-w-full" onClick={handleMessageActivate}>
          {renderMessageBubble()}
        </div>
        {!isReplyLayout && renderActionButtons()}
      </div>
      {renderReactionIcons()}
    </div>
  )
}
